import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Toggle } from './ui/toggle';
import { cn } from '../lib/utils';

/**
 * The rich-text pair used for administrator review feedback.
 *
 * WHY A DEPENDENCY AND NOT THE HOUSE TEXTAREA
 *
 * The house kit has no editor. `Textarea` is a plain control and a markdown
 * convention would put the burden of a syntax on a reviewer writing one
 * paragraph of feedback. TipTap is a real editing surface for the three marks
 * this field needs — bold, italic, lists — and nothing else is enabled, so the
 * output stays inside the allowlist the API sanitizes to.
 *
 * TRUST BOUNDARY. `RichTextView` renders stored HTML, and the value it renders
 * was written by a system administrator and read by a DIFFERENT organization's
 * users. It is already sanitized on the way into the database
 * (api/src/services/richText.ts) — that is the control. DOMPurify here is the
 * second layer, so a row that predates the sanitizer, or one written by any
 * other path, still cannot execute in a publisher's browser.
 */

/** Exactly the tags api/src/services/richText.ts allows through. */
const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li'];

/**
 * Renders stored review feedback.
 *
 * `dangerouslySetInnerHTML` is used deliberately and the value is purified
 * immediately before it: no attributes survive, so there is no href, style or
 * event-handler surface left to exploit.
 */
export const RichTextView: React.FC<{ html: string | null; className?: string }> = ({ html, className }) => {
  if (!html) return null;

  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [],
    // Belt and braces: even if a tag slipped the allowlist, its contents must
    // not be interpreted as markup.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
  });

  return (
    <div
      className={cn(
        'space-y-2 text-sm leading-relaxed [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
        className,
      )}
      // eslint-disable-next-line react/no-danger -- purified on the line above; see the note at the top of this file.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
};

interface ToolbarButtonProps {
  onClick: () => void;
  active: boolean;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
}

/**
 * A ui/toggle: pressed is the kit's selected tint, never the hover colour, and
 * Radix sets aria-pressed. `active` comes from editor.isActive, which TipTap
 * re-renders on every transaction.
 */
const ToolbarButton: React.FC<ToolbarButtonProps> = ({ onClick, active, label, icon: Icon, disabled }) => (
  <Toggle
    type="button"
    variant="ghost"
    size="icon-xs"
    aria-label={label}
    pressed={active}
    disabled={disabled}
    // preventDefault on mousedown: the default would blur the editor first,
    // and a toggle applied to a collapsed selection then lands nowhere. The
    // toggle itself runs on click (onPressedChange), so Enter and Space work.
    onMouseDown={(e) => e.preventDefault()}
    onPressedChange={() => onClick()}
  >
    <Icon aria-hidden />
  </Toggle>
);

const Toolbar: React.FC<{ editor: Editor | null; disabled?: boolean }> = ({ editor, disabled }) => {
  if (!editor) return null;
  return (
    <div className="flex items-center gap-0.5 border-b border-input px-1 py-1" role="toolbar" aria-label="Text formatting">
      <ToolbarButton
        label="Bold"
        icon={Bold}
        disabled={disabled}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        label="Italic"
        icon={Italic}
        disabled={disabled}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        label="Bulleted list"
        icon={List}
        disabled={disabled}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        label="Numbered list"
        icon={ListOrdered}
        disabled={disabled}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
    </div>
  );
};

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Spread from <Field>'s render prop so the label and errors stay wired. */
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value, onChange, placeholder, disabled, id, 'aria-invalid': ariaInvalid, 'aria-describedby': describedBy,
  'aria-required': ariaRequired,
}) => {
  const editor = useEditor({
    extensions: [
      // Only the marks the toolbar exposes. Headings, code blocks, blockquotes
      // and horizontal rules are off: the API's sanitizer would drop them
      // anyway, and an editor that accepts input it then discards is worse than
      // one that never offered it.
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
        strike: false,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      // TipTap represents "empty" as <p></p>. Reporting that as content would
      // make a rejection with no feedback look like it had some.
      onChange(e.isEmpty ? '' : e.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'min-h-[120px] w-full px-3 py-2 text-sm focus:outline-none [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5',
        ...(id ? { id } : {}),
        ...(ariaInvalid ? { 'aria-invalid': 'true' } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        ...(ariaRequired ? { 'aria-required': 'true' } : {}),
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
  });

  // Keep the editable state in step when the dialog switches to submitting.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Reset when the caller clears the field (a reopened dialog, a switched
  // version). Guarded on a real difference so typing does not fight the prop.
  useEffect(() => {
    if (editor && !value && !editor.isEmpty) {
      editor.commands.clearContent();
    }
  }, [editor, value]);

  return (
    <div
      className={cn(
        'rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        ariaInvalid && 'border-destructive-border ring-2 ring-destructive/20', // design-system-allow: mirrors ui/input's aria-invalid ring on the editor wrapper
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <Toolbar editor={editor} disabled={disabled} />
      <EditorContent editor={editor} />
      {placeholder && editor?.isEmpty ? (
        <p className="pointer-events-none -mt-[7.5rem] px-3 py-2 text-sm text-muted-foreground">{placeholder}</p>
      ) : null}
    </div>
  );
};
